/**
 * MediaAlbum/MediaGalleryAdmin.jsx  (v2 — на основі альбомів)
 * ════════════════════════════════════════════════════════════════
 * Головний компонент адмін-галереї.
 * Два стани: список альбомів ↔ редактор альбому.
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faEdit, faEye, faEyeSlash,
    faTrash, faImage, faLock, faGlobe, faPlay,
} from '@fortawesome/free-solid-svg-icons';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { constr_uri } from '../../../../../services/apiClient';
import { listAlbums, createAlbum, deleteAlbum, setAlbumPublic } from './actions';
import AlbumEditor from './AlbumEditor';
import { toast } from 'react-toastify';

// ─── Константи ────────────────────────────────────────────────────────────────

const CATEGORY_META = {
    event:    { label: 'Свято / захід', bg: '#e6f1fb', color: '#0c447c' },
    sport:    { label: 'Спорт',         bg: '#e1f5ee', color: '#085041' },
    creative: { label: 'Творчість',     bg: '#fbeaf0', color: '#72243e' },
    room:     { label: 'Приміщення',    bg: '#faeeda', color: '#633806' },
    walk:     { label: 'Прогулянка',    bg: '#eaf3de', color: '#27500a' },
    other:    { label: 'Інше',          bg: '#f1efe8', color: '#444441' },
};

const formatDate = function(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('uk-UA', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Формує текст лічильника медіа: "3 фото", "2 відео", "3 фото · 2 відео"
const mediaCountLabel = function(album) {
    var photos = album.photos_count !== undefined ? album.photos_count
        : (album.files_count || 0); // fallback для старих записів
    var videos = album.videos_count || 0;

    var parts = [];
    if (photos > 0) parts.push(photos + ' фото');
    if (videos > 0) parts.push(videos + ' відео');
    if (parts.length === 0) return '0 медіа';
    return parts.join(' · ');
};

// ─── Картка альбому ───────────────────────────────────────────────────────────

const AlbumCard = ({ album, onOpen, onTogglePublic, onDelete }) => {
    const cat = CATEGORY_META[album.category] || null;
    const hasCover = album.cover_file_id_actual && album.cover_path;

    return (
        <div style={{ border: '1px solid #dee2e6', borderRadius: 10, overflow: 'hidden', background: '#fff', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
            onMouseEnter={function(e) { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.09)'; }}
            onMouseLeave={function(e) { e.currentTarget.style.boxShadow = 'none'; }}
        >
            {/* Обкладинка */}
            <div
                style={{ width: '100%', aspectRatio: '16/9', background: '#f0f0f0', overflow: 'hidden', position: 'relative' }}
                onClick={function() { onOpen(album.id); }}
            >
                {hasCover ? (
                    <img
                        src={constr_uri('docfile?doc_id=' + album.id + '&file_id=' + album.cover_file_id_actual)}
                        alt={album.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        loading="lazy"
                    />
                ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 6, color: '#adb5bd' }}>
                        <FontAwesomeIcon icon={faImage} style={{ fontSize: 32, display: 'block', margin: '0 auto 8px', opacity: 0.4 }} />
                        <span style={{ fontSize: 14 }}>{mediaCountLabel(album)}</span>
                    </div>
                )}

                {/* Кількість медіа */}
                {hasCover && (
                    <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.55)', borderRadius: 5, padding: '2px 9px', fontSize: 12, color: '#fff', display: 'flex', alignItems: 'center', gap: 5 }}>
                        {(album.videos_count || 0) > 0 && (
                            <FontAwesomeIcon icon={faYoutube} style={{ fontSize: 11, color: '#ff6b6b' }} />
                        )}
                        {mediaCountLabel(album)}
                    </div>
                )}

                {/* Приховано */}
                {!album.is_public && (
                    <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.55)', borderRadius: 5, padding: '3px 9px', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <FontAwesomeIcon icon={faLock} style={{ fontSize: 11, color: '#fff' }} />
                        <span style={{ fontSize: 12, color: '#fff' }}>приховано</span>
                    </div>
                )}
            </div>

            {/* Інфо */}
            <div style={{ padding: '14px 16px' }}>
                <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {album.name || 'Без назви'}
                </div>
                <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                    {cat && (
                        <span style={{ background: cat.bg, color: cat.color, padding: '2px 9px', borderRadius: 8, fontSize: 12, fontWeight: 500 }}>
                            {cat.label}
                        </span>
                    )}
                    {album.event_date && (
                        <span style={{ fontSize: 11, color: '#6c757d' }}>{formatDate(album.event_date)}</span>
                    )}
                </div>

                {/* Дії */}
                <div style={{ display: 'flex', gap: 6 }}>
                    <Button variant="outline-primary" size="sm" style={{ flex: 1 }} onClick={function() { onOpen(album.id); }}>
                        <FontAwesomeIcon icon={faEdit} className="me-1" />
                        Редагувати
                    </Button>
                    <Button
                        variant={album.is_public ? 'outline-secondary' : 'outline-success'}
                        size="sm"
                        title={album.is_public ? 'Приховати альбом' : 'Опублікувати альбом'}
                        onClick={function(e) { e.stopPropagation(); onTogglePublic(album); }}
                    >
                        <FontAwesomeIcon icon={album.is_public ? faEyeSlash : faEye} />
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        title="Видалити альбом"
                        onClick={function(e) { e.stopPropagation(); onDelete(album); }}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ─── Головний компонент ───────────────────────────────────────────────────────

const MediaGalleryAdmin = ({ kgId, groups }) => {
    const [albums, setAlbums]       = useState([]);
    const [loading, setLoading]     = useState(true);
    const [creating, setCreating]   = useState(false);
    const [editingId, setEditingId] = useState(null); // null = список, string = редактор

    useEffect(function() { load(); }, [kgId]);

    const load = async function() {
        setLoading(true);
        try {
            const data = await listAlbums(kgId);
            setAlbums(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error('Помилка завантаження альбомів');
        } finally {
            setLoading(false);
        }
    };

    // ── Створити новий альбом ──────────────────────────────────────────────────

    const handleCreate = async function() {
        setCreating(true);
        try {
            const result = await createAlbum({
                kg_id:     kgId,
                name:      'Новий альбом',
                is_public: false,
            });
            if (result && result.id) {
                setEditingId(result.id);
            }
        } catch (e) {
            toast.error('Помилка створення альбому');
        } finally {
            setCreating(false);
        }
    };

    // ── Публічність ────────────────────────────────────────────────────────────

    const handleTogglePublic = async function(album) {
        var newVal = !album.is_public;
        try {
            await setAlbumPublic(album.id, newVal);
            setAlbums(function(prev) {
                return prev.map(function(a) {
                    return a.id === album.id ? Object.assign({}, a, { is_public: newVal }) : a;
                });
            });
            toast.success(newVal ? 'Альбом опубліковано' : 'Альбом приховано');
        } catch (e) {
            toast.error('Помилка');
        }
    };

    // ── Видалення ──────────────────────────────────────────────────────────────

    const handleDelete = async function(album) {
        if (!window.confirm('Видалити альбом "' + album.name + '"? Файли залишаться на сервері.')) return;
        try {
            await deleteAlbum(album.id);
            setAlbums(function(prev) { return prev.filter(function(a) { return a.id !== album.id; }); });
            toast.success('Альбом видалено');
        } catch (e) {
            toast.error('Помилка видалення');
        }
    };

    // ── Повернення зі списку ───────────────────────────────────────────────────

    const handleBack = function() {
        setEditingId(null);
        load(); // оновлюємо список
    };

    // ─── Рендер редактора ─────────────────────────────────────────────────────

    if (editingId) {
        return (
            <AlbumEditor
                albumId={editingId}
                kgId={kgId}
                groups={groups || []}
                onBack={handleBack}
            />
        );
    }

    // ─── Рендер списку ────────────────────────────────────────────────────────

    return (
        <div>
            {/* Шапка */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 500 }}>Галерея садочку</div>
                <Button variant="primary" size="sm" onClick={handleCreate} disabled={creating}>
                    {creating
                        ? <Spinner size="sm" animation="border" />
                        : <><FontAwesomeIcon icon={faPlus} className="me-1" />Новий альбом</>}
                </Button>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : albums.length === 0 ? (
                <div
                    style={{ border: '1.5px dashed #ced4da', borderRadius: 12, padding: '50px 20px', textAlign: 'center', color: '#adb5bd', cursor: 'pointer' }}
                    onClick={handleCreate}
                >
                    <FontAwesomeIcon icon={faImage} style={{ fontSize: 36, marginBottom: 12, display: 'block', margin: '0 auto 12px' }} />
                    <div style={{ fontSize: 15, marginBottom: 4 }}>Галерея порожня</div>
                    <div style={{ fontSize: 13 }}>Натисніть щоб створити перший альбом</div>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 18 }}>
                    {albums.map(function(album) {
                        return (
                            <AlbumCard
                                key={album.id}
                                album={album}
                                onOpen={setEditingId}
                                onTogglePublic={handleTogglePublic}
                                onDelete={handleDelete}
                            />
                        );
                    })}
                    {/* Кнопка новий альбом */}
                    <div
                        onClick={handleCreate}
                        style={{ border: '1.5px dashed #ced4da', borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 180, cursor: 'pointer', color: '#adb5bd', background: '#fafafa', gap: 6 }}
                    >
                        <FontAwesomeIcon icon={faPlus} style={{ fontSize: 28 }} />
                        <span style={{ fontSize: 14 }}>Новий альбом</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MediaGalleryAdmin;